import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import MicRecorder from 'mic-recorder-to-mp3';

import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent } from 'src/@/components/ui/dialog';
import { toast } from 'src/@/components/ui/use-toast';

import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import v1_actionWboTemplates from 'src/actions/v1/meta/wbo/v1_actionWboTemplates';
import campanhasService from 'src/services/campanhasService';

import { CAMPANHAS_DESIGN as D } from '../Campanhas.design';
import useCampanhaForm, { formatDataLocal } from './useCampanhaForm';
import PassoIdentificacao from './wizard/PassoIdentificacao';
import PassoPublico from './wizard/PassoPublico';
import PassoConteudo from './wizard/PassoConteudo';
import PassoAgendamento from './wizard/PassoAgendamento';

const STEPS = [
    { id: 1, label: 'Identificação', subtitle: 'Nome e canal' },
    { id: 2, label: 'Público', subtitle: 'Quem recebe' },
    { id: 3, label: 'Conteúdo', subtitle: 'A mensagem' },
    { id: 4, label: 'Agendamento', subtitle: 'Quando enviar' },
];

const normalizarComponents = (components) => {
    if (!Array.isArray(components)) return [];
    return components.map((c) => ({
        ...c,
        type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
    }));
};

const extrairVariaveisTemplate = (components) => {
    const variaveis = {};
    if (!Array.isArray(components)) return variaveis;

    const coletar = (texto) => {
        const matches = texto?.match(/\{\{(\d+)\}\}/g);
        if (matches) matches.forEach((m) => { variaveis[m.replace(/\{\{|\}\}/g, '')] = ''; });
    };

    components.forEach((component) => {
        coletar(component.text);
        (component.buttons || []).forEach((b) => coletar(b.text));
    });
    return variaveis;
};

/**
 * Analisa o CSV no navegador so para mostrar a estimativa. A fonte de verdade
 * continua sendo o backend (getContactsByCsv) — isto aqui e feedback imediato.
 */
const contarContatosCsv = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const linhas = String(e.target.result).split(/\r?\n/).filter((l) => l.trim() !== '');
                if (linhas.length < 2) return resolve({ total: 0 });

                let cabecalho = linhas[0].trim();
                if (cabecalho.charCodeAt(0) === 0xfeff) cabecalho = cabecalho.substr(1);

                const colunas = cabecalho.toLowerCase().replace(/"/g, '').split(/[;,\t]/).map((h) => h.trim());
                const idxTelefone = colunas.findIndex(
                    (h) => h.includes('numero') || h.includes('phone') || h.includes('telefone') || h.includes('celular') || h.includes('mobile')
                );
                if (idxTelefone === -1) return reject(new Error("Coluna 'numero', 'telefone' ou 'phone' não encontrada."));

                let total = 0;
                for (let i = 1; i < linhas.length; i += 1) {
                    const cols = linhas[i].trim().split(/[;,\t]/);
                    const digitos = (cols[idxTelefone] || '').replace(/\D/g, '');
                    if (digitos.length >= 10) total += 1;
                }
                return resolve({ total });
            } catch (err) {
                return reject(new Error('Falha ao processar o arquivo CSV.'));
            }
        };
        reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
        reader.readAsText(file);
    });

export default function NovaCampanhaWizard({
    open,
    onClose,
    onSalvo,
    campanhaParaEditar,
    tipoEnvioInicial,
    podeUsarSms = false,
    canais,
    isMultisession,
    clienteIdPadrao,
    configCrm,
    optionsTags,
    contactLists,
    onSelecionarPipeline,
}) {
    const { user, cliente } = useAuth();
    const { form, set, merge, reset, setVariavelTemplate, setTipoEnvio } = useCampanhaForm();

    const [passo, setPasso] = useState(1);
    const [salvando, setSalvando] = useState(false);
    const [estimativa, setEstimativa] = useState(null);
    const [loadingEstimativa, setLoadingEstimativa] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [recording, setRecording] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);

    const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);
    const isEdit = !!campanhaParaEditar;

    const clienteIdCanal = useMemo(
        () => (isMultisession ? form.canalEnvio : cliente._id),
        [isMultisession, form.canalEnvio, cliente._id]
    );

    const isWhatsAppBusiness = useMemo(() => {
        const alvoId = isMultisession ? form.canalEnvio : cliente._id;
        const canal = (canais || []).find(
            (c) => String(c.cliente_id || c.clienteId) === String(alvoId)
        );
        return canal?.platform === 'whatsapp_business';
    }, [form.canalEnvio, canais, isMultisession, cliente._id]);

    // Abertura: reseta e, em edicao, hidrata a partir da campanha.
    useEffect(() => {
        if (!open) return;
        setPasso(1);
        setEstimativa(null);

        const tipoEnvioPadrao = tipoEnvioInicial === 'sms' && !podeUsarSms ? 'whatsapp' : (tipoEnvioInicial || 'whatsapp');
        const base = {
            canalEnvio: isMultisession ? clienteIdPadrao || '' : '',
            tipoEnvio: tipoEnvioPadrao,
        };

        if (!campanhaParaEditar) {
            reset(base);
            return;
        }

        const c = campanhaParaEditar;
        const dataEnvio = c.data_envio ? new Date(c.data_envio) : null;
        reset({
            ...base,
            nome: c.nome || '',
            tipoEnvio: c.tipo_envio === 'sms' ? 'sms' : 'whatsapp',
            canalEnvio: c.cliente_id || base.canalEnvio,
            tipo: c.tipo || '',
            funilIds: c.funil_ids_content || [],
            tagsIds: c.tags_ids_content || [],
            selectedContactLists: c.contact_list_ids || [],
            isFiltroData: c.isFiltroData ?? true,
            dataInicio: c.data_inicio ? c.data_inicio.split('T')[0] : base.dataInicio,
            dataFinal: c.data_final ? c.data_final.split('T')[0] : base.dataFinal,
            body: c.texto || '',
            isFile: !!c.file,
            isChatAtendimento: !!c.isChatAtendimento,
            dataCampanha: dataEnvio ? formatDataLocal(dataEnvio) : undefined,
            horaCampanha: dataEnvio ? String(dataEnvio.getHours()).padStart(2, '0') : '',
            minutoCampanha: dataEnvio ? String(dataEnvio.getMinutes()).padStart(2, '0') : '00',
            useTemplate: !!c.config?.useTemplate,
            templateSelecionado: c.config?.templateSelecionado || null,
            templateVariaveis: c.config?.templateVariaveis || {},
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, campanhaParaEditar]);

    // Templates WABA.
    useEffect(() => {
        if (!isWhatsAppBusiness || !clienteIdCanal) return;
        let cancelado = false;
        setLoadingTemplates(true);
        v1_actionWboTemplates(clienteIdCanal)
            .then((res) => { if (!cancelado) setTemplates(res.data?.data?.templates || []); })
            .catch(() => {})
            .finally(() => { if (!cancelado) setLoadingTemplates(false); });
        return () => { cancelado = true; };
    }, [isWhatsAppBusiness, clienteIdCanal]);

    // Estimativa (debounce), so no passo 2.
    useEffect(() => {
        if (passo !== 2 || !form.tipo) return undefined;
        if (form.tipo === 'csv' || form.tipo === 'lista_contatos') return undefined;
        if (form.tipo === 'funil' && (!form.funilId || form.funilIds.length === 0)) {
            setEstimativa(null);
            return undefined;
        }
        if (form.tipo === 'tag' && form.tagsIds.length === 0) {
            setEstimativa(null);
            return undefined;
        }

        const timer = setTimeout(async () => {
            setLoadingEstimativa(true);
            try {
                const clienteAlvo = isMultisession ? form.canalEnvio : cliente._id;
                const res = await campanhasService.estimativa(clienteAlvo, {
                    funil_ids: form.tipo === 'funil' ? form.funilIds.map((f) => f._id) : [],
                    tag_ids: form.tipo === 'tag' ? form.tagsIds.map((t) => t._id || t.id) : [],
                    isFiltroData: form.isFiltroData,
                    dataInicio: form.dataInicio,
                    dataFinal: form.dataFinal,
                });
                const data = res?.data;
                setEstimativa(
                    typeof data === 'object' && data !== null && 'total' in data
                        ? data.total
                        : data?.qty || data?.count || data || 0
                );
            } catch (err) {
                setEstimativa(0);
            } finally {
                setLoadingEstimativa(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [
        passo, form.tipo, form.funilId, form.funilIds, form.tagsIds, form.isFiltroData,
        form.dataInicio, form.dataFinal, form.canalEnvio, isMultisession, cliente._id,
    ]);

    const handleCsvChange = async (file) => {
        set('csvFile', file);
        if (!file) return setEstimativa(null);
        setLoadingEstimativa(true);
        try {
            const { total } = await contarContatosCsv(file);
            setEstimativa(total);
            toast({ title: 'Arquivo analisado', description: `${total} contatos válidos identificados.` });
        } catch (err) {
            set('csvFile', null);
            setEstimativa(0);
            toast({ title: 'Erro no CSV', description: err.message, variant: 'destructive' });
        } finally {
            setLoadingEstimativa(false);
        }
        return undefined;
    };

    const handleSelecionarTemplate = (templateId) => {
        if (!templateId || templateId === 'none') {
            return merge({ templateSelecionado: null, templateVariaveis: {} });
        }
        const template = templates.find((t) => (t.templateId || t._id) === templateId);
        if (!template) return undefined;

        const comps = normalizarComponents(template.components || []);
        return merge({
            templateSelecionado: {
                templateId: template.templateId || template._id,
                name: template.name || template.templateName,
                conteudo: comps.find((c) => c.type === 'BODY')?.text || '',
                components: comps,
            },
            templateVariaveis: extrairVariaveisTemplate(comps),
        });
    };

    const handleStartRecording = async () => {
        setLoadingAudio(true);
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            await recorder.start();
            setRecording(true);
        } catch (err) {
            toast({ title: 'Erro', description: 'Não foi possível acessar o microfone.', variant: 'destructive' });
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleStopRecording = async () => {
        try {
            const [buffer, blob] = await recorder.stop().getMp3();
            set('audioGravado', new File(buffer, 'audio.mp3', { type: blob.type, lastModified: Date.now() }));
        } catch (err) {
            toast({ title: 'Erro', description: 'Falha ao finalizar a gravação.', variant: 'destructive' });
        } finally {
            setRecording(false);
        }
    };

    const validarPasso = () => {
        if (passo === 1) {
            if (!form.nome.trim()) return 'Informe o nome da campanha.';
            if (isMultisession && !form.canalEnvio) return 'Selecione o canal de envio.';
        }
        if (passo === 2) {
            if (!form.tipo) return 'Escolha de onde vêm os destinatários.';
            if (form.tipo === 'funil' && (!form.funilId || form.funilIds.length === 0)) return 'Selecione o pipeline e ao menos um estágio.';
            if (form.tipo === 'tag' && form.tagsIds.length === 0) return 'Selecione ao menos uma tag.';
            if (form.tipo === 'lista_contatos' && form.selectedContactLists.length === 0) return 'Selecione ao menos uma lista.';
            if (form.tipo === 'csv' && !form.csvFile && !isEdit) return 'Faça o upload do arquivo CSV.';
            if (form.isFiltroData && ['funil', 'tag'].includes(form.tipo) && (!form.dataInicio || !form.dataFinal)) {
                return 'Informe as datas do filtro de período.';
            }
        }
        if (passo === 3) {
            if (form.tipoEnvio === 'sms') {
                if (!form.body.trim()) return 'SMS exige um texto.';
            } else {
                if (form.useTemplate && !form.templateSelecionado) return 'Selecione um template.';
                if (!form.useTemplate && !form.body && !form.isFile) return 'A campanha não tem mensagem nem arquivo.';
                if (form.isFile && form.files.length === 0 && !form.audioGravado) {
                    return 'Você marcou "Enviar mídia", mas não anexou nada.';
                }
            }
        }
        return null;
    };

    const handleAvancar = () => {
        const erro = validarPasso();
        if (erro) return toast({ title: 'Atenção', description: erro, variant: 'destructive' });
        return setPasso((p) => p + 1);
    };

    /**
     * Faz upload e RETORNA o array. O codigo antigo mutava um `const filesUrl = []`
     * declarado no corpo do componente e lia `payload.files` antes do upload
     * acontecer — o resultado e que a primeira campanha com midia ia sem os arquivos.
     */
    const uploadArquivos = async () => {
        const enviados = [];
        for (const file of form.files) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                // eslint-disable-next-line no-await-in-loop
                const res = await v2_actionUploadS3(formData, '?path=plataforma/campanhas');
                if (!res.data.error) enviados.push({ key: res.data.data.key, type: file.type });
            } catch (err) {
                throw new Error(`Falha ao enviar o arquivo ${file.name}.`);
            }
        }
        return enviados;
    };

    const uploadAudio = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const res = await v2_actionUploadS3(formData, '?path=plataforma/agendamentos');
        if (res.data.error) throw new Error('Falha ao enviar o áudio.');
        return [{ key: res.data.data.key, type: file.type, tipo: 'audio' }];
    };

    const handleSalvar = async () => {
        if (!form.horaCampanha || !form.minutoCampanha) {
            return toast({ title: 'Atenção', description: 'Defina o horário do envio.', variant: 'destructive' });
        }

        setSalvando(true);
        try {
            const payload = {
                nome: form.nome,
                tipoEnvio: form.tipoEnvio,
                dataCampanha: new Date(form.dataCampanha),
                horaCampanha: form.horaCampanha,
                minutoCampanha: form.minutoCampanha,
                funilIds: form.funilIds,
                tagsIds: form.tagsIds,
                isFiltroData: form.isFiltroData,
                dataInicio: form.dataInicio,
                dataFinal: form.dataFinal,
                status_envio: 'agendado',
                criado_por: user.id,
                cliente_id: isMultisession ? form.canalEnvio : cliente._id,
                tipo: form.tipo,
                texto: form.body,
                isFile: form.isFile,
                files: [],
                isChatAtendimento: form.isChatAtendimento,
                useTemplate: form.useTemplate,
                templateSelecionado: form.templateSelecionado,
                templateVariaveis: form.templateVariaveis,
                contactListIds: form.selectedContactLists.map((l) => l._id || l),
            };

            if (form.tipo === 'csv' && form.csvFile) {
                const formData = new FormData();
                formData.append('file', form.csvFile);
                const res = await v2_actionUploadS3(formData, '?path=plataforma/campanhas/csv');
                if (res.data.error) throw new Error('Erro ao fazer upload do CSV.');
                payload.csvUrl =
                    res.data.data.location ||
                    res.data.data.url ||
                    `${import.meta.env.VITE_AWS_URL}/${res.data.data.key}`;
            }

            if (form.isFile) {
                if (form.tipoArquivo === 'audio' && form.audioGravado) {
                    payload.files = await uploadAudio(form.audioGravado);
                } else if (form.files.length > 0) {
                    payload.files = await uploadArquivos();
                }
            }

            let aviso = null;
            if (isEdit) {
                await campanhasService.editar(campanhaParaEditar._id, {
                    ...payload,
                    action: form.isFile ? 'edit_info_with_file' : 'edit_info_without_file',
                });
            } else {
                const resposta = await campanhasService.criar(payload);
                aviso = resposta?.aviso || null;
            }

            toast({
                title: 'Sucesso',
                description: `Campanha ${isEdit ? 'editada' : 'criada'} com sucesso.`,
            });

            // O backend agora reporta quando a campanha nasceu sem destinatarios —
            // antes isso respondia 200 limpo e a campanha simplesmente nunca disparava.
            if (aviso) {
                toast({ title: 'Atenção', description: aviso, variant: 'destructive' });
            }

            onSalvo(form.canalEnvio);
        } catch (error) {
            toast({
                title: 'Erro',
                description: error?.mensagem || error?.message || 'Falha ao salvar a campanha.',
                variant: 'destructive',
            });
        } finally {
            setSalvando(false);
        }
        return undefined;
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className={D.wizard.dialog}>
                <div className={D.wizard.header}>
                    <p className="text-base font-bold text-gray-900 dark:text-[#D1D2D3]">
                        {isEdit ? 'Editar campanha' : 'Nova campanha'}
                    </p>

                    <div className={`${D.wizard.stepper} mt-3`}>
                        {STEPS.map((s, i) => (
                            <div key={s.id} className="flex items-center gap-2 flex-1 last:flex-none">
                                <div
                                    className={
                                        passo === s.id ? D.wizard.stepAtivo
                                            : passo > s.id ? D.wizard.stepFeito
                                                : D.wizard.stepInativo
                                    }
                                >
                                    {passo > s.id ? <Iconify icon="eva:checkmark-fill" width={13} /> : s.id}
                                </div>
                                <div className="hidden sm:block min-w-0">
                                    <p className={D.wizard.stepLabel}>{s.label}</p>
                                    <p className={D.wizard.stepSub}>{s.subtitle}</p>
                                </div>
                                {i < STEPS.length - 1 && <div className={D.wizard.stepLinha} />}
                            </div>
                        ))}
                    </div>
                </div>

                <div className={D.wizard.corpo}>
                    {passo === 1 && (
                        <PassoIdentificacao
                            form={form}
                            set={set}
                            setTipoEnvio={setTipoEnvio}
                            podeUsarSms={podeUsarSms}
                            canais={canais}
                            isMultisession={isMultisession}
                            isEdit={isEdit}
                        />
                    )}
                    {passo === 2 && (
                        <PassoPublico
                            form={form}
                            set={set}
                            configCrm={configCrm}
                            optionsTags={optionsTags}
                            contactLists={contactLists}
                            estimativa={estimativa}
                            loadingEstimativa={loadingEstimativa}
                            isWhatsAppBusiness={isWhatsAppBusiness}
                            isEdit={isEdit}
                            onCsvChange={handleCsvChange}
                            onSelecionarPipeline={onSelecionarPipeline}
                        />
                    )}
                    {passo === 3 && (
                        <PassoConteudo
                            form={form}
                            set={set}
                            setVariavelTemplate={setVariavelTemplate}
                            templates={templates}
                            loadingTemplates={loadingTemplates}
                            isWhatsAppBusiness={isWhatsAppBusiness}
                            onSelecionarTemplate={handleSelecionarTemplate}
                            onFilesChange={(e) => set('files', Array.from(e.target.files || []))}
                            recording={recording}
                            loadingAudio={loadingAudio}
                            onStartRecording={handleStartRecording}
                            onStopRecording={handleStopRecording}
                        />
                    )}
                    {passo === 4 && <PassoAgendamento form={form} set={set} estimativa={estimativa} />}
                </div>

                <div className={D.wizard.footer}>
                    <Button
                        variant="ghost"
                        onClick={passo === 1 ? onClose : () => setPasso((p) => p - 1)}
                        disabled={salvando}
                        className="text-xs"
                    >
                        {passo === 1 ? 'Cancelar' : 'Voltar'}
                    </Button>

                    {passo < STEPS.length ? (
                        <Button onClick={handleAvancar} className={D.page.actionButton}>
                            Continuar
                            <Iconify icon="eva:arrow-ios-forward-fill" width={14} className="ml-1" />
                        </Button>
                    ) : (
                        <Button onClick={handleSalvar} disabled={salvando} className={D.page.actionButton}>
                            {salvando && <Iconify icon="mdi:loading" width={14} className="animate-spin mr-1.5" />}
                            {isEdit ? 'Salvar alterações' : 'Criar campanha'}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

NovaCampanhaWizard.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSalvo: PropTypes.func.isRequired,
    campanhaParaEditar: PropTypes.object,
    tipoEnvioInicial: PropTypes.string,
    podeUsarSms: PropTypes.bool,
    canais: PropTypes.array,
    isMultisession: PropTypes.bool,
    clienteIdPadrao: PropTypes.string,
    configCrm: PropTypes.object,
    optionsTags: PropTypes.array,
    contactLists: PropTypes.array,
    onSelecionarPipeline: PropTypes.func.isRequired,
};
